import { useAuth } from 'hooks/useAuth';
import useFetch from 'hooks/useFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  atualizarLista as atualizarListaService,
  ConfirmacaoPresencaAdmin,
  CriarListaPayload,
  criarLista as criarListaService,
  encerrarLista as encerrarListaService,
  listarConfirmacoesPresencaAdmin,
  listarMinhasListas,
  listarTemplates,
  obterPixKeys,
  salvarPixKey as salvarPixKeyService,
  trocarTemplateLista as trocarTemplateListaService,
  verificarSlugDisponivel,
  VerificarSlugResponse,
} from 'services/adminListas';
import {
  ListaResumo,
  ListaTemplateResumo,
  ListaTemplateSugestaoPresente,
  PixKeyResumo,
} from 'types';
import { trackCreateListSuccess } from 'utils/analytics';
import { BUILD_PROFILE } from 'utils/buildProfile';
import { setGoogleAdsUserData, trackGoogleAdsConversion } from 'utils/googleTag';
import { recordError, recordEvent, withTiming } from 'utils/observability';
import { normalizePixKeyOnBlur, PixKeyInfo } from 'utils/pix';
import { gerarSlug, sugerirSlugAutomatico } from 'utils/slug';
import { trackEvent } from 'utils/tracking';

const getSlugPrefixByBuild = () => {
  if (BUILD_PROFILE.brand === 'chadebebe') return 'c';
  if (BUILD_PROFILE.brand === 'chadebb') return 'b';
  if (BUILD_PROFILE.brand === 'listacasamento') return 'l';
  return 'p';
};

const gerarSlugAleatorio = () => {
  const sufixo =
    Math.random()
      .toString(36)
      .replace(/[^a-z0-9]+/gi, '')
      .slice(-6) || 'lista01';
  return `${getSlugPrefixByBuild()}-${sufixo}`;
};

export const useAdminListasRules = () => {
  const MAX_LISTAS = 1;
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user?.isAdmin;
  const isGuestUser = user?.authProvider === 'guest';
  const masterAdminEmails = useMemo(() => {
    const raw = process.env.REACT_APP_ADMIN_EMAILS || '';
    return raw
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }, []);
  const isMasterAdmin = useMemo(() => {
    const email = user?.email?.toLowerCase();
    if (!email) return isAdmin;
    return isAdmin || masterAdminEmails.includes(email);
  }, [isAdmin, masterAdminEmails, user?.email]);

  const {
    data: listasData,
    status: statusListas,
    error: erroListas,
    run: fetchListas,
  } = useFetch<[], ListaResumo[]>(listarMinhasListas, {
    immediate: true,
    timeoutMs: 12000,
  });

  const { data: templatesData, isLoading: isLoadingTemplates } = useFetch<
    [],
    ListaTemplateResumo[]
  >(listarTemplates, {
    immediate: true,
    timeoutMs: 12000,
  });

  const listas = useMemo(() => listasData ?? [], [listasData]);
  const templates = useMemo(() => templatesData ?? [], [templatesData]);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    tituloLista: '',
    templateSlug: BUILD_PROFILE.forceTemplateSlug ?? 'cha-de-bebe',
    publica: false,
    pixKey: '',
    dataEvento: '',
  });
  const [createPixKeyInfo, setCreatePixKeyInfo] = useState<PixKeyInfo | null>(null);
  const [privateSlugPreview, setPrivateSlugPreview] = useState('');
  const [slugConflitoMensagem, setSlugConflitoMensagem] = useState('');
  const [slugSufixo, setSlugSufixo] = useState('');
  const [slugDisponivel, setSlugDisponivel] = useState<boolean | null>(null);
  const [checandoSlug, setChecandoSlug] = useState(false);
  const [slugSugestao, setSlugSugestao] = useState('');
  const [ultimoSlugVerificado, setUltimoSlugVerificado] = useState('');

  const templateSelecionado = useMemo(
    () => templates.find((template) => template.slug === createForm.templateSlug) ?? null,
    [templates, createForm.templateSlug]
  );

  const [selectedList, setSelectedList] = useState<ListaResumo | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [pixHistory, setPixHistory] = useState<PixKeyResumo[]>([]);
  const [loadingPixHistory, setLoadingPixHistory] = useState(false);
  const [pixInput, setPixInput] = useState('');
  const [pixKeyInfo, setPixKeyInfo] = useState<PixKeyInfo | null>(null);
  const [pixFeedback, setPixFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const [editForm, setEditForm] = useState({
    tituloLista: '',
    dataEvento: '',
  });
  const [rsvpDialogOpen, setRsvpDialogOpen] = useState(false);
  const [saveDateDialogOpen, setSaveDateDialogOpen] = useState(false);
  const [rsvpForm, setRsvpForm] = useState({
    enabled: false,
    eventDate: '',
  });
  const [saveDateForm, setSaveDateForm] = useState({
    enabled: false,
    eventDate: '',
    eventTime: '',
    location: '',
    headline: '',
    description: '',
  });
  const [savingList, setSavingList] = useState(false);
  const [changingTemplate, setChangingTemplate] = useState(false);
  const [confirmacoesPresenca, setConfirmacoesPresenca] = useState<ConfirmacaoPresencaAdmin[]>([]);
  const [loadingConfirmacoesPresenca, setLoadingConfirmacoesPresenca] = useState(false);
  const [tipoListaInput, setTipoListaInput] = useState('');

  const slugsExistentes = useMemo(() => listas.map((lista) => lista.slug), [listas]);
  const slugPreviewAutomatico = useMemo(() => {
    if (!createForm.publica) {
      return '';
    }
    return sugerirSlugAutomatico(createForm.tituloLista, slugsExistentes);
  }, [createForm.publica, createForm.tituloLista, slugsExistentes]);

  const slugPublicoCompleto = useMemo(() => {
    if (!createForm.publica) return '';
    const base = slugPreviewAutomatico;
    if (!base) return '';
    const sufixoLimpo = gerarSlug(slugSufixo);
    return sufixoLimpo ? `${base}-${sufixoLimpo}` : base;
  }, [createForm.publica, slugPreviewAutomatico, slugSufixo]);

  const slugPrivadoPreview = useMemo(
    () => privateSlugPreview || gerarSlugAleatorio(),
    [privateSlugPreview]
  );

  const formatIsoToLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (v: number) => String(v).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const toIsoStringOrNull = (value: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const formatIsoToDateInput = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  const toIsoDateStringOrNull = (value: string) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const validarDisponibilidadeSlug = useCallback(
    async (
      slugOpcional?: string,
      forcar: boolean = false
    ): Promise<VerificarSlugResponse | null> => {
      const slugAlvo = (slugOpcional ?? slugPublicoCompleto ?? slugPreviewAutomatico ?? '').trim();

      if (!createForm.publica || !slugAlvo) {
        setSlugDisponivel(null);
        setSlugConflitoMensagem('');
        setSlugSugestao('');
        return null;
      }

      if (!forcar && slugAlvo === ultimoSlugVerificado && slugDisponivel !== null) {
        return {
          disponivel: slugDisponivel,
          slug: slugAlvo,
          sugestao: slugSugestao || undefined,
        };
      }

      setChecandoSlug(true);
      setUltimoSlugVerificado(slugAlvo);

      try {
        const resultado = await verificarSlugDisponivel(slugAlvo);
        setSlugDisponivel(resultado.disponivel);
        setSlugSugestao(resultado.sugestao ?? '');
        setSlugConflitoMensagem(
          resultado.disponivel
            ? ''
            : resultado.sugestao
              ? `O identificador /${slugAlvo} já está em uso. Sugestão: ${resultado.sugestao}`
              : `O identificador /${slugAlvo} já está em uso.`
        );
        return resultado;
      } catch (err: any) {
        const message =
          err?.response?.data?.error || 'Não foi possível validar o identificador agora.';
        setSlugDisponivel(null);
        setSlugSugestao('');
        setSlugConflitoMensagem(message);
        return null;
      } finally {
        setChecandoSlug(false);
      }
    },
    [
      createForm.publica,
      slugPublicoCompleto,
      slugPreviewAutomatico,
      slugDisponivel,
      slugSugestao,
      ultimoSlugVerificado,
    ]
  );

  useEffect(() => {
    if (erroListas) {
      setError('Erro ao carregar listas');
    }
  }, [erroListas]);

  // Usuários não-admin são redirecionados direto para a página da lista
  useEffect(() => {
    if (isMasterAdmin) return;
    if (statusListas !== 'success') return;
    const slug = listas[0]?.slug;
    if (!slug) return;
    navigate(`/${slug}`, { replace: true });
  }, [isMasterAdmin, statusListas, listas, navigate]);

  // Auto-seleciona a única lista do usuário comum ao carregar (admin master)
  useEffect(() => {
    if (!isMasterAdmin) return;
    if (selectedList) return;
    if (listas.length === 0) return;
    void abrirDialogEditar(listas[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listas, isMasterAdmin]);

  useEffect(() => {
    if (!successMessage) return;
    const timeout = window.setTimeout(() => setSuccessMessage(''), 5000);
    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    if (!pixFeedback || pixFeedback.severity !== 'success') return;
    const timeout = window.setTimeout(() => setPixFeedback(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [pixFeedback]);

  useEffect(() => {
    if (!createForm.publica) {
      setSlugDisponivel(null);
      setSlugConflitoMensagem('');
      setSlugSugestao('');
      return;
    }

    const slugAlvo = slugPublicoCompleto || slugPreviewAutomatico;
    if (!slugAlvo) {
      setSlugDisponivel(null);
      setSlugConflitoMensagem('');
      setSlugSugestao('');
      return;
    }

    const timeout = window.setTimeout(() => {
      void validarDisponibilidadeSlug(slugAlvo);
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [createForm.publica, slugPublicoCompleto, slugPreviewAutomatico, validarDisponibilidadeSlug]);

  const abrirDialogCriar = () => {
    setCreateForm({
      tituloLista: '',
      templateSlug: BUILD_PROFILE.forceTemplateSlug ?? 'cha-de-bebe',
      publica: false,
      pixKey: '',
      dataEvento: '',
    });
    setCreatePixKeyInfo(null);
    setPrivateSlugPreview(gerarSlugAleatorio());
    setSlugConflitoMensagem('');
    setSlugSufixo('');
    setSlugDisponivel(null);
    setSlugSugestao('');
    setUltimoSlugVerificado('');
    setChecandoSlug(false);
    setCreating(false);
    setCreateDialogOpen(true);
    trackEvent('create_list_dialog_open', {
      source: 'admin_listas',
      isGuestUser,
    });
  };

  const fecharDialogCriar = () => {
    if (creating) return;
    setCreateDialogOpen(false);
    trackEvent('create_list_dialog_close', {
      source: 'admin_listas',
    });
  };

  const handleTituloChange = (valor: string) => {
    setSlugConflitoMensagem('');
    setSlugSufixo('');
    setSlugDisponivel(null);
    setSlugSugestao('');
    setUltimoSlugVerificado('');
    setCreateForm((prev) => ({ ...prev, tituloLista: valor }));
  };

  const handlePixKeyChange = (valor: string) => {
    setCreateForm((prev) => ({ ...prev, pixKey: valor }));
    setCreatePixKeyInfo(null);
  };

  const handlePixKeyBlur = () => {
    const { value, info } = normalizePixKeyOnBlur(createForm.pixKey);
    if (value !== createForm.pixKey) {
      setCreateForm((prev) => ({ ...prev, pixKey: value }));
    }
    if (!value) {
      setCreatePixKeyInfo(null);
      return;
    }
    setCreatePixKeyInfo(info);
  };

  const handleDataEventoChange = (valor: string) => {
    setCreateForm((prev) => ({ ...prev, dataEvento: valor }));
  };

  const handleSlugSufixoChange = (valor: string) => {
    setSlugSufixo(valor);
    setSlugDisponivel(null);
    setUltimoSlugVerificado('');
  };

  const handleTituloBlur = () => {
    void validarDisponibilidadeSlug(undefined, true);
  };

  const handleTemplateChange = (templateSlug: string) => {
    if (BUILD_PROFILE.forceTemplateSlug) {
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      templateSlug,
    }));
  };

  const criarLista = async () => {
    const tituloLista = createForm.tituloLista.trim();
    const slug = slugPrivadoPreview.trim() || gerarSlugAleatorio();
    const pixKeyInicial = normalizePixKeyOnBlur(createForm.pixKey).value;

    const templateSlugEfetivo = BUILD_PROFILE.forceTemplateSlug ?? createForm.templateSlug;

    if (!tituloLista) {
      trackEvent('create_list_blocked_validation', {
        source: 'admin_listas',
        hasTitulo: !!tituloLista,
      });
      recordEvent('list_created', {
        status: 'validation_failed',
        reason: 'missing_title',
        userEmail: user?.email,
      });
      setError('Informe o título da lista.');
      return;
    }

    setCreating(true);

    const payload: CriarListaPayload = {
      tituloLista,
      publica: false,
      tipoLista: templateSlugEfetivo || 'personalizado',
      slug,
    };

    if (templateSlugEfetivo) {
      payload.templateSlug = templateSlugEfetivo;
    }

    try {
      const data = await withTiming('create_list_api', () => criarListaService(payload), {
        userEmail: user?.email,
        templateSlug: templateSlugEfetivo,
      });
      const templateAplicado = data?.templateAplicado;
      const slugGerado: string | undefined = data?.slugGerado ?? data?.lista?.slug;
      const listaCriada = data?.lista;
      const sugestoesTemplate: ListaTemplateSugestaoPresente[] = data?.sugestoes?.template ?? [];
      const sugestoesAfiliados: Array<{ id: string }> = data?.sugestoes?.afiliados ?? [];
      const totalSugestoes = sugestoesTemplate.length + sugestoesAfiliados.length;
      const visibilidade: 'publica' | 'privada' | undefined = data?.visibilidade;
      let pixMensagem = '';

      if (listaCriada?.id && pixKeyInicial.length >= 5) {
        try {
          await salvarPixKeyService(listaCriada.id, pixKeyInicial);
          pixMensagem = ' Chave PIX cadastrada para esta lista.';
        } catch (pixErr: any) {
          pixMensagem = ' Você pode cadastrar ou ajustar a chave PIX depois.';
        }
      } else if (pixKeyInicial.length > 0) {
        pixMensagem = ' Você pode cadastrar ou ajustar a chave PIX depois.';
      } else {
        pixMensagem = ' Cadastre a chave PIX depois, quando quiser.';
      }

      let mensagem = templateAplicado
        ? `Lista criada com sucesso com o modelo "${templateAplicado.titulo}".`
        : 'Lista criada com sucesso!';
      if (totalSugestoes > 0) {
        mensagem += ` Você tem ${totalSugestoes} sugestões prontas para adicionar à sua lista.`;
      }
      if (slugGerado) {
        mensagem +=
          visibilidade === 'privada'
            ? ` Compartilhe com cuidado: o link privado é /${slugGerado}.`
            : ` Disponível em /${slugGerado}.`;
      }
      mensagem += `${pixMensagem || ' Cadastre a chave PIX e personalize seus itens.'}`;
      setError('');
      setCreateDialogOpen(false);
      void fetchListas();

      if (listaCriada?.id) {
        trackEvent('create_list_success', {
          source: 'admin_listas',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo || 'personalizado',
        });
        trackCreateListSuccess({
          source: 'admin_listas',
          listId: listaCriada.id,
          templateSlug: templateSlugEfetivo || 'personalizado',
          brand: BUILD_PROFILE.brand,
        });
        recordEvent('list_created', {
          status: 'success',
          listId: listaCriada.id,
          listTitle: tituloLista,
          templateSlug: templateSlugEfetivo,
          slug: slugGerado,
          userEmail: user?.email,
        });
        setGoogleAdsUserData({
          email: user?.email,
          phoneNumber: user?.telefone,
          fullName: user?.nome,
        });
        trackGoogleAdsConversion(
          BUILD_PROFILE.googleAdsId,
          BUILD_PROFILE.googleAdsConversionLabel,
          listaCriada.id
        );
        setSuccessMessage(mensagem);
        navigate('/admin/listas', {
          state: { mensagemSucesso: 'Lista criada! Agora escolha os presentes para publicar.' },
        });
      } else {
        setSuccessMessage(mensagem);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao criar lista';
      trackEvent('create_list_error', {
        source: 'admin_listas',
        templateSlug: templateSlugEfetivo || 'personalizado',
        status: err?.response?.status,
        error: message,
      });
      recordError(err, {
        action: 'create_list',
        listTitle: tituloLista,
        templateSlug: templateSlugEfetivo,
        userEmail: user?.email,
        errorMessage: message,
      });
      setError(message);
      if (message.toLowerCase().includes('slug')) {
        setSlugConflitoMensagem(message);
      }
    } finally {
      setCreating(false);
    }
  };

  const abrirDialogEditar = async (lista: ListaResumo) => {
    const config = lista.configuracoesDetalhes ?? {};
    const dataCompartilhada =
      (typeof config.rsvpEventDate === 'string' && config.rsvpEventDate) ||
      (typeof config.saveDateEventDate === 'string' && config.saveDateEventDate) ||
      lista.dataCasamento ||
      null;
    const rsvpEnabledDefault = typeof config.enableRsvp === 'boolean' ? config.enableRsvp : true;
    const saveDateEnabledDefault =
      typeof config.enableSaveTheDate === 'boolean' ? config.enableSaveTheDate : true;

    setSelectedList(lista);
    setEditDialogOpen(false);
    setEditForm({
      tituloLista: lista.tituloLista,
      dataEvento: formatIsoToLocalInput(lista.dataCasamento),
    });
    setPixInput(lista.pixKeyAtual?.chave ?? '');
    setPixKeyInfo(null);
    setTipoListaInput(lista.tipoLista ?? lista.template?.slug ?? '');
    setRsvpForm({
      enabled: rsvpEnabledDefault,
      eventDate: formatIsoToDateInput(dataCompartilhada),
    });
    setSaveDateForm({
      enabled: saveDateEnabledDefault,
      eventDate: formatIsoToDateInput(dataCompartilhada),
      eventTime: typeof config.saveDateEventTime === 'string' ? config.saveDateEventTime : '',
      location: typeof config.saveDateLocation === 'string' ? config.saveDateLocation : '',
      headline: typeof config.saveDateHeadline === 'string' ? config.saveDateHeadline : '',
      description: typeof config.saveDateDescription === 'string' ? config.saveDateDescription : '',
    });
    setPixHistory([]);
    setConfirmacoesPresenca([]);
    setPixFeedback(null);
    setLoadingPixHistory(true);
    setLoadingConfirmacoesPresenca(true);

    try {
      const [pixResponse, presencaResponse] = await Promise.all([
        obterPixKeys(lista.id),
        listarConfirmacoesPresencaAdmin(lista.id),
      ]);

      setPixHistory(pixResponse.historico ?? []);
      if (pixResponse.pixChaveAtiva) {
        setPixInput(pixResponse.pixChaveAtiva.chave);
        setPixKeyInfo(normalizePixKeyOnBlur(pixResponse.pixChaveAtiva.chave).info);
      }

      setConfirmacoesPresenca(presencaResponse.confirmacoes ?? []);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao carregar dados da lista para edição.';
      setPixFeedback({ severity: 'error', message });
    } finally {
      setLoadingPixHistory(false);
      setLoadingConfirmacoesPresenca(false);
    }
  };

  const fecharDialogEditar = () => {
    if (savingList) return;
    setEditDialogOpen(false);
    setSelectedList(null);
    setPixHistory([]);
    setConfirmacoesPresenca([]);
    setPixFeedback(null);
  };

  const abrirDialogPresenca = (lista: ListaResumo) => {
    void abrirDialogEditar(lista);
    setRsvpDialogOpen(true);
  };

  const fecharDialogPresenca = () => {
    if (savingList) return;
    setRsvpDialogOpen(false);
  };

  const abrirDialogSaveTheDate = (lista: ListaResumo) => {
    void abrirDialogEditar(lista);
    setSaveDateDialogOpen(true);
  };

  const fecharDialogSaveTheDate = () => {
    if (savingList) return;
    setSaveDateDialogOpen(false);
  };

  const handleRsvpToggle = (enabled: boolean) => {
    setRsvpForm((prev) => ({ ...prev, enabled }));
  };

  const handleRsvpDateChange = (eventDate: string) => {
    setRsvpForm((prev) => ({ ...prev, eventDate }));
  };

  const handleSaveDateToggle = (enabled: boolean) => {
    setSaveDateForm((prev) => ({ ...prev, enabled }));
  };

  const handleSaveDateChange = (field: keyof typeof saveDateForm, value: string | boolean) => {
    setSaveDateForm((prev) => ({ ...prev, [field]: value }));
  };

  const refreshSelectedList = async () => {
    if (!selectedList) return;
    const listasAtualizadas = (await fetchListas()) ?? [];
    const listaAtualizada = listasAtualizadas.find((item) => item.id === selectedList.id);
    if (listaAtualizada) {
      await abrirDialogEditar(listaAtualizada);
    }
  };

  const salvarConfiguracaoPresenca = async () => {
    if (!selectedList) return;
    setSavingList(true);
    try {
      await atualizarListaService(selectedList.id, {
        enableRsvp: rsvpForm.enabled,
        rsvpEventDate: rsvpForm.eventDate ? toIsoDateStringOrNull(rsvpForm.eventDate) : null,
      });
      setSuccessMessage('Configuração de presença salva com sucesso.');
      setPixFeedback(null);
      await refreshSelectedList();
      setRsvpDialogOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao salvar configuração de presença';
      setPixFeedback({ severity: 'error', message });
    } finally {
      setSavingList(false);
    }
  };

  const salvarConfiguracaoSaveTheDate = async () => {
    if (!selectedList) return;
    setSavingList(true);
    try {
      await atualizarListaService(selectedList.id, {
        enableSaveTheDate: saveDateForm.enabled,
        saveDateEventDate: saveDateForm.eventDate
          ? toIsoDateStringOrNull(saveDateForm.eventDate)
          : null,
        saveDateEventTime: saveDateForm.eventTime || null,
        saveDateLocation: saveDateForm.location || null,
        saveDateHeadline: saveDateForm.headline || null,
        saveDateDescription: saveDateForm.description || null,
      });
      setSuccessMessage('Save the Date salvo com sucesso.');
      setPixFeedback(null);
      await refreshSelectedList();
      setSaveDateDialogOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao salvar Save the Date';
      setPixFeedback({ severity: 'error', message });
    } finally {
      setSavingList(false);
    }
  };

  const salvarAlteracoes = async () => {
    if (!selectedList) return;
    const tituloLista = editForm.tituloLista.trim();
    const dataEventoIso = toIsoStringOrNull(editForm.dataEvento);
    const chave = pixInput.trim();
    const tituloAtual = selectedList.tituloLista.trim();
    const pixAtual = (selectedList.pixKeyAtual?.chave || '').trim();
    const tituloAlterado = tituloLista !== tituloAtual;
    const pixAlterada = !!chave && chave !== pixAtual;

    if (!tituloLista) {
      setPixFeedback({ severity: 'error', message: 'Informe o título da lista.' });
      return;
    }

    setSavingList(true);

    try {
      await atualizarListaService(selectedList.id, {
        tituloLista,
        dataEvento: dataEventoIso,
      });

      if (chave) {
        try {
          const response = await salvarPixKeyService(selectedList.id, chave);
          setPixHistory(response.historico ?? []);
          if (response.pixChaveAtiva) {
            setPixInput(response.pixChaveAtiva.chave);
          }
        } catch (pixErr: any) {
          const messagePix = pixErr?.response?.data?.error || 'Erro ao salvar chave PIX';
          setPixFeedback({ severity: 'error', message: messagePix });
        }
      }

      setSuccessMessage('Alterações salvas com sucesso.');
      await fetchListas();

      if (tituloAlterado || pixAlterada) {
        setGoogleAdsUserData({
          email: user?.email,
          phoneNumber: user?.telefone,
          fullName: user?.nome,
        });
        trackGoogleAdsConversion(
          BUILD_PROFILE.googleAdsId,
          BUILD_PROFILE.googleAdsEditListConversionLabel,
          `${selectedList.id}-edit`
        );
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao atualizar a lista';
      setPixFeedback({ severity: 'error', message });
    } finally {
      setSavingList(false);
    }
  };

  const handlePixInputChange = (valor: string) => {
    setPixInput(valor);
    setPixKeyInfo(null);
    if (pixFeedback?.severity === 'error') {
      setPixFeedback(null);
    }
  };

  const handlePixInputBlur = () => {
    const { value, info } = normalizePixKeyOnBlur(pixInput);
    if (value !== pixInput) {
      setPixInput(value);
    }
    if (!value) {
      setPixKeyInfo(null);
      return;
    }
    setPixKeyInfo(info);
  };

  const handleEditTituloChange = (valor: string) => {
    setEditForm((prev) => ({ ...prev, tituloLista: valor }));
  };

  const handleEditDataEventoChange = (valor: string) => {
    setEditForm((prev) => ({ ...prev, dataEvento: valor }));
  };

  const abrirGerenciarPresentes = (lista: ListaResumo) => {
    trackEvent('open_manage_gifts', {
      source: 'admin_listas',
      listId: lista.id,
      slug: lista.slug,
    });
    navigate(`/admin/listas/${lista.id}/presentes`);
  };

  const abrirPresentesRecebidos = (lista: ListaResumo) => {
    trackEvent('open_received_gifts', {
      source: 'admin_listas',
      listId: lista.id,
      slug: lista.slug,
    });
    navigate(`/admin/listas/${lista.id}/recebidos`);
  };

  const abrirCatalogoAfiliados = () => {
    navigate('/admin/produtos-afiliados');
  };

  const encerrarLista = async (lista: ListaResumo) => {
    const confirmar = window.confirm(
      `Encerrar a lista "${lista.tituloLista}"? Ela não ficará mais disponível para convidados.`
    );
    if (!confirmar) return;

    try {
      await encerrarListaService(lista.id);
      setSuccessMessage('Lista encerrada com sucesso.');
      setError('');
      await fetchListas();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao encerrar a lista';
      setError(message);
    }
  };

  const encerrarListaSelecionada = async () => {
    if (!selectedList) return;
    await encerrarLista(selectedList);
    fecharDialogEditar();
  };

  const mudarTipoLista = async (novoSlug: string) => {
    if (!selectedList) return;
    if (novoSlug === (selectedList.tipoLista ?? selectedList.template?.slug ?? '')) return;

    const confirmar = window.confirm(
      'Essa alteração irá remover todos os presentes atuais da lista. Deseja continuar?'
    );
    if (!confirmar) {
      setTipoListaInput(selectedList.tipoLista ?? selectedList.template?.slug ?? '');
      return;
    }

    setSavingList(true);
    setChangingTemplate(true);
    try {
      const result = await trocarTemplateListaService(selectedList.id, novoSlug);
      setTipoListaInput(result.templateSlug);

      const listasAtualizadas = (await fetchListas()) ?? [];
      const listaAtualizada = listasAtualizadas.find((item) => item.id === selectedList.id);
      if (listaAtualizada) {
        await abrirDialogEditar(listaAtualizada);
      }

      setPixFeedback(null);
      setSuccessMessage('Modelo da lista atualizado com sucesso.');
    } catch (err: any) {
      const message =
        err?.response?.data?.error || err?.message || 'Erro ao trocar o modelo da lista';
      setPixFeedback({ severity: 'error', message });
      setTipoListaInput(selectedList.tipoLista ?? selectedList.template?.slug ?? '');
    } finally {
      setChangingTemplate(false);
      setSavingList(false);
    }
  };

  const handleTipoListaChange = (novoSlug: string) => {
    setTipoListaInput(novoSlug);
    void mudarTipoLista(novoSlug);
  };

  const carregandoListas = statusListas === 'idle' || statusListas === 'loading';

  return {
    MAX_LISTAS,
    isAdmin,
    isGuestUser,
    isMasterAdmin,
    listas,
    templates,
    statusListas,
    carregandoListas,
    isLoadingTemplates,
    error,
    successMessage,
    createDialogOpen,
    creating,
    createForm,
    createPixKeyInfo,
    editForm,
    slugPreviewAutomatico,
    slugPublicoCompleto,
    slugPrivadoPreview,
    slugConflitoMensagem,
    slugDisponivel,
    checandoSlug,
    slugSugestao,
    slugSufixo,
    privateSlugPreview,
    templateSelecionado,
    editDialogOpen,
    rsvpDialogOpen,
    saveDateDialogOpen,
    pixHistory,
    loadingPixHistory,
    pixInput,
    pixKeyInfo,
    pixFeedback,
    selectedList,
    savingList,
    changingTemplate,
    confirmacoesPresenca,
    loadingConfirmacoesPresenca,
    rsvpForm,
    saveDateForm,
    setSuccessMessage,
    setError,
    abrirDialogCriar,
    fecharDialogCriar,
    handleTituloChange,
    handleSlugSufixoChange,
    handlePixKeyChange,
    handlePixKeyBlur,
    handleDataEventoChange,
    handleTituloBlur,
    handleTemplateChange,
    criarLista,
    abrirDialogEditar,
    fecharDialogEditar,
    abrirDialogPresenca,
    fecharDialogPresenca,
    abrirDialogSaveTheDate,
    fecharDialogSaveTheDate,
    handleRsvpToggle,
    handleRsvpDateChange,
    handleSaveDateToggle,
    handleSaveDateChange,
    salvarConfiguracaoPresenca,
    salvarConfiguracaoSaveTheDate,
    salvarAlteracoes,
    handlePixInputChange,
    handlePixInputBlur,
    handleEditTituloChange,
    handleEditDataEventoChange,
    abrirGerenciarPresentes,
    abrirPresentesRecebidos,
    abrirCatalogoAfiliados,
    encerrarLista,
    encerrarListaSelecionada,
    tipoListaInput,
    handleTipoListaChange,
  };
};

export type UseAdminListasRulesReturn = ReturnType<typeof useAdminListasRules>;
